import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';

import DataTable from '@/components/ui/DataTable/DataTable';
import { Col } from '@/components/ui/DataTable/ColumnsHelpers';
import Button from '@/components/ui/Button';
import Alert from '@/components/ui/Alert';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog/ConfirmationDialog';
import { useDeleteAuthApi, useGetAuthApi, usePostAuthApi } from '@/hooks/UseApiCalls';
import {
  BANK_COLLECTION_LOCKS_URL,
  BankCollectionLockDtoIn,
  CollectionRow,
  collectionRows,
  fromIsoDate,
} from './bankCollectionLocks';

const PREFIX = 'bankCollectionLocks';

/**
 * Lifting a lock before its day: the one step here with a consequence worth a question - once the
 * file is at the bank, a change made now does not reach that collection. Mounted per date, so the
 * address it deletes is fixed while it is open.
 */
const ReleaseLockDialog = ({ date, label, onClose, onDone }: { date: string; label: string; onClose: () => void; onDone: () => void }) => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const { mutate: release, isPending } = useDeleteAuthApi<void>(`${BANK_COLLECTION_LOCKS_URL}/${date}`);

  return (
    <ConfirmationDialog
      open
      onOpenChange={(open) => { if (!open && !isPending) onClose(); }}
      title={t(`${PREFIX}.releaseTitle`, { date: label })}
      message={t(`${PREFIX}.releaseMessage`, { date: label })}
      confirmText={isPending ? 'saving' : `${PREFIX}.releaseConfirm`}
      cancelText={`${PREFIX}.cancel`}
      onConfirm={() => {
        setError(null);
        release(undefined, {
          onSuccess: () => { onDone(); onClose(); },
          onError: () => setError(t(`${PREFIX}.errorSaving`)),
        });
      }}
      onCancel={onClose}
      isLoading={isPending}
      error={error}
    />
  );
};

/**
 * «נעילת מס"ב» (HAT-1441): the coming collection dates, each open or locked. The office locks a date
 * before it sends that day's file to the bank; from then until the day has passed, coordinators
 * cannot change a bank standing order that collects on it. Locking takes no question - it holds
 * nothing that cannot be lifted a moment later; lifting asks first.
 */
const BankCollectionLocksPage = () => {
  const { t, i18n } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [lockingDate, setLockingDate] = useState<string | null>(null);
  const [releaseDate, setReleaseDate] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: locks, isLoading, isError, refetch } = useGetAuthApi<BankCollectionLockDtoIn[]>(BANK_COLLECTION_LOCKS_URL);
  const { mutate: lock } = usePostAuthApi<BankCollectionLockDtoIn, { collectionDate: string }>(BANK_COLLECTION_LOCKS_URL);

  const rows = useMemo(() => collectionRows(locks), [locks]);

  const locale = i18n.language === 'en' ? 'en-GB' : 'he-IL';
  const dayLabel = (date: string) =>
    new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'numeric', year: 'numeric' }).format(fromIsoDate(date));
  const shortLabel = (date: string) =>
    new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'numeric' }).format(fromIsoDate(date));
  const lockedWhen = (utc: string) =>
    new Date(utc).toLocaleString(locale, { timeZone: 'Asia/Jerusalem', day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' });

  const handleLock = (date: string) => {
    setError(null);
    setLockingDate(date);
    lock({ collectionDate: date }, {
      onSuccess: () => refetch(),
      onError: () => setError(t(`${PREFIX}.errorSaving`)),
      onSettled: () => setLockingDate(null),
    });
  };

  const columns: ColumnDef<CollectionRow>[] = [
    {
      id: 'date',
      header: Col(t(`${PREFIX}.date`)),
      accessorKey: 'date',
      cell: ({ row }) => <span className="body-md-regular">{dayLabel(row.original.date)}</span>,
      enableSorting: false,
      meta: { className: 'min-w-[200px]' },
    },
    {
      id: 'status',
      header: Col(t(`${PREFIX}.status`)),
      enableSorting: false,
      cell: ({ row }) => {
        const locked = row.original.lock != null;
        return (
          <span
            className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium"
            style={locked
              ? { color: 'var(--status-pending-text)', backgroundColor: 'var(--status-pending-bg)' }
              : { color: 'var(--status-approved-text)', backgroundColor: 'var(--status-approved-bg)' }}
          >
            {t(locked ? `${PREFIX}.locked` : `${PREFIX}.open`)}
          </span>
        );
      },
      meta: { className: 'min-w-[130px] max-w-[130px]' },
    },
    {
      id: 'lockedBy',
      header: Col(t(`${PREFIX}.lockedByColumn`)),
      enableSorting: false,
      cell: ({ row }) => {
        const { lock: current } = row.original;
        if (!current) return <span className="text-(--font-body-3)">-</span>;
        return (
          <span className="body-md-regular">
            {t(`${PREFIX}.lockedBy`, { name: current.lockedBy ?? '', when: lockedWhen(current.lockedAt) })}
          </span>
        );
      },
      meta: { className: 'min-w-[220px]' },
    },
    {
      id: 'actions',
      header: Col(t(`${PREFIX}.actions`)),
      enableSorting: false,
      cell: ({ row }) => {
        const { date, lock: current } = row.original;
        return current ? (
          <Button variant="outline" onClick={(e) => { e.stopPropagation(); setReleaseDate(date); }}>
            {t(`${PREFIX}.release`)}
          </Button>
        ) : (
          <Button
            variant="primary"
            disabled={lockingDate != null}
            onClick={(e) => { e.stopPropagation(); handleLock(date); }}
          >
            {t(lockingDate === date ? `${PREFIX}.locking` : `${PREFIX}.lock`)}
          </Button>
        );
      },
      meta: { className: 'min-w-[140px]', lockLayout: true },
    },
  ];

  return (
    <div>
      {(error || isError) && <Alert type="error" message={error ?? t(`${PREFIX}.errorLoading`)} />}
      <DataTable
        title={t(`${PREFIX}.title`)}
        titleCaption={<span className="body-md-regular text-(--font-body-2)">{t(`${PREFIX}.intro`)}</span>}
        data={rows}
        isLoading={isLoading}
        columns={columns}
        getRowId={(row) => row.date}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        customGlobalSearch={() => true}
      />
      {releaseDate && (
        <ReleaseLockDialog
          key={releaseDate}
          date={releaseDate}
          label={shortLabel(releaseDate)}
          onClose={() => setReleaseDate(null)}
          onDone={() => refetch()}
        />
      )}
    </div>
  );
};

export default BankCollectionLocksPage;
